import {stageDisplayName} from '../journal/stage.js';
import {liveTimerSnapshot} from './stage-timer.js';
import {createLiveTimeline} from './timeline.js';

function active(rows=[]){
  return (rows||[]).filter(row=>!row?.deletedAt);
}

function byOrder(a,b){
  return Number(a?.sortOrder||0)-Number(b?.sortOrder||0)||String(a?.id||'').localeCompare(String(b?.id||''));
}

function latest(rows=[]){
  return [...(rows||[])].sort((a,b)=>
    Date.parse(a?.recordedAt||0)-Date.parse(b?.recordedAt||0)
    ||String(a?.id||'').localeCompare(String(b?.id||''))
  ).at(-1)||null;
}

function required(value,label){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  return text;
}

export function createLiveDashboardModel({
  bundle={},
  now=Date.now,
  timelineLimit=60,
  stageControlBlocked=false
}={}){
  const session=bundle?.session;
  if(!session||String(session.status||'')!=='smoking')throw new TypeError('Live dashboard requires a smoking session');

  const stages=active(bundle.stages).sort(byOrder);
  const activeStageId=required(session.activeStageId,'activeStageId');
  const currentIndex=stages.findIndex(stage=>stage.id===activeStageId);
  if(currentIndex<0)throw new Error('LIVE_ACTIVE_STAGE_NOT_FOUND');

  const currentStage=stages[currentIndex];
  const nextStage=stages[currentIndex+1]||null;
  const stageTarget=(bundle.stageTargets||[]).find(row=>row.stageId===currentStage.id)||null;
  const timer=liveTimerSnapshot({session,stageTarget,now});
  const items=active(bundle.items).sort(byOrder);
  const readings=bundle.temperatureReadings||[];
  const latestChamber=latest(readings.filter(row=>row.readingKind==='chamber'));
  const itemTargets=new Map((bundle.itemTargets||[])
    .filter(row=>row.stageId===currentStage.id)
    .map(row=>[row.itemId,row]));

  const internalTemperatures=Object.freeze(items.map(item=>Object.freeze({
    itemId:item.id,
    name:item.nameSnapshot,
    latest:latest(readings.filter(row=>row.readingKind==='internal'&&row.itemId===item.id)),
    target:itemTargets.get(item.id)?.targetInternalCelsius??null
  })));

  const woods=active(bundle.woods).sort(byOrder);
  const itemNames=new Map(items.map(item=>[item.id,item.nameSnapshot]));
  const captures=[
    ...(bundle.liveNotes||[]).map(note=>({
      id:note.id,
      kind:'note',
      capturedAt:note.capturedAt,
      text:note.text,
      stageId:note.stageId||null,
      itemId:note.itemId||null,
      itemName:note.itemId?itemNames.get(note.itemId)||null:null,
      syncStatus:note.syncStatus||null
    })),
    ...(bundle.photos||[]).map(photo=>({
      id:photo.id,
      kind:'photo',
      capturedAt:photo.capturedAt||photo.createdAt||null,
      text:null,
      stageId:photo.stageId||null,
      itemId:photo.itemId||null,
      itemName:photo.itemId?itemNames.get(photo.itemId)||null:null,
      photoStatus:photo.photoStatus||null,
      syncStatus:photo.syncStatus||null
    }))
  ]
    .filter(row=>row.capturedAt&&!Number.isNaN(Date.parse(row.capturedAt)))
    .sort((a,b)=>Date.parse(b.capturedAt)-Date.parse(a.capturedAt)||String(b.id).localeCompare(String(a.id)));

  const liveStartedAt=Date.parse(session.actualStartAt||'');
  const recentCaptures=Object.freeze(captures
    .filter(row=>Number.isNaN(liveStartedAt)||Date.parse(row.capturedAt)>=liveStartedAt)
    .slice(0,6)
    .map(row=>Object.freeze(row)));
  const timeline=createLiveTimeline({bundle:{...bundle,session},limit:timelineLimit});
  const stageChoices=Object.freeze(stages.map((stage,index)=>Object.freeze({
    id:stage.id,
    name:stageDisplayName(stage,{locale:'pl'}),
    relation:index<currentIndex?'previous':index===currentIndex?'current':index===currentIndex+1?'next':'later'
  })));

  return Object.freeze({
    sessionId:session.id,
    sessionTitle:String(session.title||'').trim()||'Wędzenie bez nazwy',
    stageControlBlocked:Boolean(stageControlBlocked),
    products:Object.freeze(items.map(item=>Object.freeze({id:item.id,name:item.nameSnapshot}))),
    timer,
    currentStage:Object.freeze({
      id:currentStage.id,
      name:stageDisplayName(currentStage,{locale:'pl'}),
      target:stageTarget
    }),
    nextStage:nextStage?Object.freeze({
      id:nextStage.id,
      name:stageDisplayName(nextStage,{locale:'pl'})
    }):null,
    stageChoices,
    latestChamber,
    internalTemperatures,
    woodChoices:Object.freeze(woods.map(wood=>Object.freeze({id:wood.id,name:wood.nameSnapshot}))),
    recentCaptures,
    timeline,
    quickActions:Object.freeze([
      Object.freeze({id:'temperature',label:'Temperatura'}),
      Object.freeze({id:'event',label:'Drewno / zdarzenie'}),
      Object.freeze({id:'photo',label:'Zdjęcie'}),
      Object.freeze({id:'note',label:'Notatka'})
    ]),
    source:Object.freeze({
      actualStartAt:session.actualStartAt,
      activeStageStartedAt:session.activeStageStartedAt,
      activeStageId:session.activeStageId
    })
  });
}
